import { Box, Card, Grid, Typography } from '@mui/material'
import { PhotoConsumer } from 'react-photo-view'
import ReactPlayer from 'react-player'
import { saveAs } from 'file-saver'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import TableChartIcon from '@mui/icons-material/TableChart'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

const CommentAttachedFileCard = ({ attachments, baseUrl, cvv, user }) => {
  const token = localStorage.getItem('token')
  const tokenNubi = localStorage.getItem('tokenNubi')
  const tokenStr = localStorage.getItem('tokenStr')

  const getFileExtension = (path) => {
    if (!path) return null
    const parts = path.split('.')
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : null
  }

  const buildUrl = (path) => {
    return `${baseUrl}${path}&token=${cvv && (user?.razonSocial_p === 'Strong Systems SRL' ? tokenStr : tokenNubi) || ''}${!cvv && token || ''}`
  }

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 28, color: '#e53935' }} />
      case 'doc':
      case 'docx':
        return <DescriptionIcon sx={{ fontSize: 28, color: '#1976d2' }} />
      case 'xls':
      case 'xlsx':
        return <TableChartIcon sx={{ fontSize: 28, color: '#43a047' }} />
      case 'txt':
        return <TextSnippetIcon sx={{ fontSize: 28, color: '#757575' }} />
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 28, color: '#9e9e9e' }} />
    }
  }

  const renderImageFile = (img, extension) => {
    const url = buildUrl(img.path_ic)
    return (
      <Grid item xs={6} sm={4} md={3} lg={2.4} key={`IMG${img.path_ic}`}>
        <PhotoConsumer src={url} intro={img.path_ic}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
              p: 0.5,
              borderRadius: 1.5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                borderColor: '#1976d2'
              }
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: 60,
                borderRadius: 1,
                overflow: 'hidden',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5
              }}
            >
              <img
                src={url}
                alt={img?.nombre_ic}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#424242',
                fontSize: 8,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
            >
              {img?.nombre_ic}
            </Typography>
          </Card>
        </PhotoConsumer>
      </Grid>
    )
  }

  const renderVideoFile = (img, extension) => {
    const url = buildUrl(img.path_ic)
    return (
      <Grid item xs={12} sm={6} md={4} key={`Video${img.path_ic}`}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 0.5,
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
            }
          }}
        >
          <Box
            sx={{
              borderRadius: 1,
              overflow: 'hidden',
              background: '#000',
              mb: 0.5
            }}
          >
            <ReactPlayer url={url} width="100%" height="80px" controls />
          </Box>
          <Typography
            sx={{
              color: '#fff',
              fontSize: 8,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}
          >
            {img?.nombre_ic}
          </Typography>
        </Card>
      </Grid>
    )
  }

  const renderDocumentFile = (img, extension) => {
    const url = buildUrl(img.path_ic)
    
    const gradientColors = {
      pdf: { bg: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', border: '#ef9a9a' },
      doc: { bg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '#90caf9' },
      docx: { bg: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '#90caf9' },
      xls: { bg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '#a5d6a7' },
      xlsx: { bg: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '#a5d6a7' },
      txt: { bg: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '#ffcc80' }
    }

    const colors = gradientColors[extension] || { bg: '#f5f5f5', border: '#e0e0e0' }

    return (
      <Grid item xs={6} sm={4} md={3} lg={2.4} key={`Doc${img.path_ic}`}>
        <Card
          sx={{
            background: colors.bg,
            p: 0.5,
            borderRadius: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
            }
          }}
          onClick={() => saveAs(url, img.path_ic)}
        >
          <Box
            sx={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.5
            }}
          >
            {getFileIcon(extension)}
          </Box>
          <Typography
            sx={{
              color: '#424242',
              fontSize: 8,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              width: '100%'
            }}
          >
            {img?.nombre_ic}
          </Typography>
          <Typography
            sx={{
              color: '#757575',
              fontSize: 7,
              fontWeight: 500,
              textTransform: 'uppercase',
              mt: 0.25
            }}
          >
            {extension}
          </Typography>
        </Card>
      </Grid>
    )
  }

  const renderFile = (img) => {
    if (!img?.path_ic) return null

    const extension = getFileExtension(img.path_ic)
    if (!extension) return null

    // Imágenes
    const imageExtensions = ['jpg', 'png', 'jpeg', 'svg', 'x-png', 'gif', 'bmp', 'webp']
    if (imageExtensions.includes(extension)) {
      return renderImageFile(img, extension)
    }

    // Videos
    const videoExtensions = ['wmv', 'mp4', 'avi', 'mov', 'mkv', 'flv']
    if (videoExtensions.includes(extension)) {
      return renderVideoFile(img, extension)
    }

    // Documentos
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt']
    if (documentExtensions.includes(extension)) {
      return renderDocumentFile(img, extension)
    }

    // Otros archivos
    return renderDocumentFile(img, extension)
  }

  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <Grid container spacing={0.75} sx={{ mt: 0.5 }}>
      {attachments.map((img) => renderFile(img))}
    </Grid>
  )
}

export default CommentAttachedFileCard
